"use client";

/**
 * MediaGalleryGrid — read-only display of a mixed image/GIF/video gallery.
 * Shared by the artist page Gallery tab and the user profile Gallery tab so
 * both entity types get identical viewing behavior (Gordon 2026-09-15: "add
 * more than one image, gif or mp4 to artist and user profile"). GIFs render
 * through the plain <img> branch — only video needs its own path.
 *
 * Grid tiles autoplay video muted/looped (Instagram-grid convention); the
 * lightbox (same z-[200] bg-black/95 idiom as the Sync Reel viewer) plays
 * video with sound + controls.
 */

import { useCallback, useEffect, useState } from "react";
import NextImage from "next/image";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { isVideoUrl } from "@/lib/media-kind";

export interface MediaGalleryGridProps {
  items: string[];
  /** Used for alt text / aria-labels, e.g. the artist or user's display name. */
  altPrefix: string;
  className?: string;
}

export function MediaGalleryGrid({ items, altPrefix, className }: MediaGalleryGridProps) {
  const media = items.filter(Boolean);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const close = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => {
    setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : media.length - 1));
  }, [media.length]);
  const next = useCallback(() => {
    setLightboxIndex((i) => (i !== null && i < media.length - 1 ? i + 1 : 0));
  }, [media.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIndex, close, prev, next]);

  if (media.length === 0) return null;

  return (
    <>
      <div className={cn("grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2", className)}>
        {media.map((url, i) => {
          const video = isVideoUrl(url);
          return (
            <button
              key={i}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
              aria-label={`View ${altPrefix} media ${i + 1} of ${media.length}`}
            >
              {video ? (
                <>
                  <video
                    src={url}
                    muted
                    loop
                    autoPlay
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute bottom-1.5 right-1.5 z-10 bg-black/60 text-white rounded-full p-1">
                    <Play className="h-3 w-3 fill-white" />
                  </div>
                </>
              ) : (
                <NextImage
                  src={url}
                  alt={`${altPrefix} ${i + 1}`}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                />
              )}
            </button>
          );
        })}
      </div>

      {lightboxIndex !== null && media[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={`${altPrefix} media lightbox`}
        >
          <div
            className="relative flex items-center justify-center w-full h-full max-w-5xl max-h-full px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideoUrl(media[lightboxIndex]) ? (
              <video
                src={media[lightboxIndex]}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={media[lightboxIndex]}
                alt={`${altPrefix} ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
              />
            )}

            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs rounded-full px-3 py-1">
              {lightboxIndex + 1} / {media.length}
            </div>
          </div>

          {media.length > 1 && (
            <button
              type="button"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {media.length > 1 && (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          <button
            type="button"
            className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
            onClick={close}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}

export default MediaGalleryGrid;
